"use client";

import React from "react";
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  Share2, 
  Facebook, 
  Twitter, 
  Linkedin,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="11349" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-sm bg-muted/20" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex h-full flex-col overflow-hidden border-border bg-card shadow-sm transition-all hover:shadow-md rounded-sm" data-index={i}>
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium tracking-wider text-muted-foreground uppercase">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(post.date)}</span>
            </div>
            <CardTitle className="line-clamp-2 text-xl font-bold leading-tight tracking-tighter">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
              {post.excerpt ? (
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Explore the strategic implications of this AI infrastructure development and its impact on the investment landscape."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group rounded-sm border-primary/20 hover:border-primary transition-all">
                Examine Brief
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId)], 
    category__in: categoryIds,
    orderby: "date",
    order: "DESC",
  });

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between border-b border-border pb-4">
        <h3 className="text-2xl font-bold tracking-tighter md:text-3xl">Strategic Insights</h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex hover:bg-accent rounded-sm">
            Access Engine <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery} className="w-full">
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="space-y-4">
          <div className="h-8 w-3/4 animate-pulse rounded-sm bg-muted/20" />
          <div className="h-4 w-1/2 animate-pulse rounded-sm bg-muted/20" />
          <div className="mt-8 aspect-video w-full animate-pulse rounded-sm bg-muted/20" />
          <div className="mt-8 space-y-2">
            <div className="h-4 w-full animate-pulse rounded-sm bg-muted/20" />
            <div className="h-4 w-full animate-pulse rounded-sm bg-muted/20" />
            <div className="h-4 w-2/3 animate-pulse rounded-sm bg-muted/20" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 py-12 text-center">
        <h2 className="text-2xl font-bold tracking-tighter">Intelligence Briefing Unavailable</h2>
        <p className="mt-2 text-muted-foreground">The requested analysis is currently restricted or has been reclassified within our network.</p>
        <Link to="#" className="mt-6">
          <Button className="rounded-sm">Back to Thesis</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map((c) => c.id) || [];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      
      <header className="mb-8 text-center md:mb-16">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          {post.categories && post.categories.length > 0 && (
            <span className="rounded-none bg-primary px-3 py-1 text-primary-foreground">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(post.date)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            8 min briefing
          </span>
        </div>

        <h1 className="mb-8 text-4xl font-bold leading-tight tracking-tighter text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>
      </header>

      
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-sm shadow-xl md:mb-20">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      
      <div 
        className="prose prose-lg prose-neutral dark:prose-invert mx-auto max-w-none 
        prose-headings:font-bold prose-headings:tracking-tighter prose-a:text-primary prose-a:no-underline hover:prose-a:underline
        prose-img:rounded-sm prose-img:shadow-lg prose-p:leading-relaxed prose-p:text-foreground/90"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      
      <div className="mt-16 flex flex-col gap-6 border-y border-border py-10 md:mt-24 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <span className="mr-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Classifications:</span>
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"}>
                <span className="rounded-none bg-muted px-2.5 py-1 text-xs font-medium text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-index={i}>
                  {tag.name}
                </span>
              </Link>
            ))
          ) : (
            <span className="text-sm italic text-muted-foreground">General Intelligence</span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <Share2 className="h-4 w-4" /> Disseminate:
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-border hover:border-primary hover:text-primary">
              <Facebook className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-border hover:border-primary hover:text-primary">
              <Twitter className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-border hover:border-primary hover:text-primary">
              <Linkedin className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      
      <RelatedPostsWrapper currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || { post_type: "post", p: 1 };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}